import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import { useDispatch, useSelector } from "react-redux";
import { DeleteOrders, getAllOrders } from "../../actions/orderAction";
import { clearError } from "../../slices/orderSlice";
import Loader from "../Loader";
import { MdDelete, MdReceipt, MdPerson, MdEmail, MdPhone, MdLocationOn } from "react-icons/md";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const Order = () => {
    const {
        adminOrders = [],
        loading = true,
        error,
        isOrderDeleted,
    } = useSelector((state) => state.orderState);
    const dispatch = useDispatch();
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [open, setOpen] = useState(null);

    useEffect(() => {
        if (error) {
            toast(error, {
                position: toast.POSITION.BOTTOM_CENTER,
                type: "error",
                onOpen: () => {
                    dispatch(clearError());
                },
            });
            return;
        }
        dispatch(getAllOrders());
    }, [dispatch, error, isOrderDeleted]);

    useEffect(() => {
        setFilteredOrders(
            adminOrders.filter((order) =>
                (order.OrderId || "").toString().toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [adminOrders, searchQuery]);

    const handleDelete = (e, id) => {
        e.stopPropagation(); // Prevent accordion from toggling
        Swal.fire({
            title: "Delete Order?",
            text: "This action will permanently remove the transaction record.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#D4AF37",
            cancelButtonColor: "#1A1A1A",
            confirmButtonText: "Confirm Delete",
            background: "#ffffff",
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(DeleteOrders(id));
            }
        });
    };

    const calculateItemPrice = (item) => {
        return (item.price / 8 * item.weight) + (item.price * item.makingrate / 100);
    };

    return (
        <div className="flex bg-gray-50 min-h-screen">
            <Sidebar />
            
            <div className="flex-1 p-8 lg:p-12 overflow-x-hidden">
                {loading ? (
                    <Loader />
                ) : (
                    <div className="max-w-7xl mx-auto">
                        {/* Header Section */}
                        <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 gap-6">
                            <div>
                                <h1 className="text-3xl font-serif font-bold text-gray-900 mb-2">Order Management</h1>
                                <p className="text-gray-500 text-sm font-light">Review and manage your boutique's latest transactions.</p>
                            </div>

                            <div className="relative group min-w-[320px]">
                                <input
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    type="text"
                                    className="w-full bg-white border border-gray-100 px-6 py-4 rounded-2xl text-sm focus:border-yellow-600 outline-none transition-all shadow-sm group-hover:shadow-md"
                                    placeholder="Search by Order ID..."
                                />
                                <div className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-300">
                                    <MdReceipt className="text-xl" />
                                </div>
                            </div>
                        </div>

                        {/* Orders Grid/Table */}
                        <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="border-b border-gray-50 bg-gray-50/30">
                                            <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Order Reference</th>
                                            <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Client Name</th>
                                            <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</th>
                                            <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {filteredOrders.length > 0 ? (
                                            filteredOrders.slice().reverse().map((order) => (
                                                <React.Fragment key={order.OrderId}>
                                                    <tr 
                                                        onClick={() => setOpen(open === order.OrderId ? null : order.OrderId)}
                                                        className={`hover:bg-gray-50/50 cursor-pointer transition-colors ${open === order.OrderId ? 'bg-gray-50/80 shadow-inner' : ''}`}
                                                    >
                                                        <td className="px-8 py-6">
                                                            <div className="font-bold text-gray-900 font-serif">#{order.OrderId}</div>
                                                            <div className="text-[10px] text-gray-400 mt-1 uppercase tracking-widest">
                                                                Placed on: {new Date(order.createdAt).toLocaleDateString()}
                                                            </div>
                                                        </td>
                                                        <td className="px-8 py-6">
                                                            <div className="font-bold text-gray-800 text-sm">{order.shippingInfo.fullname}</div>
                                                            <div className="text-xs text-gray-400 font-light">{order.shippingInfo.phone_No}</div>
                                                        </td>
                                                        <td className="px-8 py-6">
                                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-yellow-100 text-yellow-700 border border-yellow-200">
                                                                New Order
                                                            </span>
                                                        </td>
                                                        <td className="px-8 py-6 text-center">
                                                            <button 
                                                                onClick={(e) => handleDelete(e, order._id)}
                                                                className="p-3 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                                                title="Remove Order"
                                                            >
                                                                <MdDelete className="text-xl" />
                                                            </button>
                                                        </td>
                                                    </tr>

                                                    {/* Expansion Details */}
                                                    {open === order.OrderId && (
                                                        <tr>
                                                            <td colSpan="4" className="px-8 pb-10 bg-gray-50/80">
                                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-4 animate-fadeIn">
                                                                    {/* Shipping Details */}
                                                                    <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm space-y-6">
                                                                        <div className="flex items-center gap-3 text-yellow-600 mb-2">
                                                                            <MdPerson className="text-xl" />
                                                                            <h3 className="text-xs font-bold uppercase tracking-widest">Customer Insight</h3>
                                                                        </div>
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 text-sm font-medium">
                                                                            <div className="flex items-center gap-2 text-gray-700">
                                                                                <MdEmail className="text-gray-300" /> {order.shippingInfo.email}
                                                                            </div>
                                                                            <div className="flex items-center gap-2 text-gray-700">
                                                                                <MdPhone className="text-gray-300" /> {order.shippingInfo.phone_No}
                                                                            </div>
                                                                            <div className="md:col-span-2 flex items-start gap-2 text-gray-600 font-light italic mt-2">
                                                                                <MdLocationOn className="text-gray-300 mt-1" />
                                                                                <span>
                                                                                    {order.shippingInfo.house_No}, {order.shippingInfo.street},<br/>
                                                                                    {order.shippingInfo.city}, {order.shippingInfo.district} - {order.shippingInfo.postalCode}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Order Items Details */}
                                                                    <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
                                                                        <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-6">Collection Review ({order.orderItems.length})</h3>
                                                                        <div className="space-y-6">
                                                                            {order.orderItems.map((item) => (
                                                                                <div key={item.product} className="flex items-center gap-5">
                                                                                    <div className="w-14 h-14 bg-gray-50 rounded-xl overflow-hidden border border-gray-50 shrink-0">
                                                                                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                                                                                    </div>
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <div className="text-sm font-bold text-gray-900 truncate font-serif">{item.name}</div>
                                                                                        <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">
                                                                                            {item.quantity} Unit · {item.weight} Grams
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="text-sm font-bold text-yellow-600">
                                                                                        Rs {calculateItemPrice(item).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                            <div className="pt-6 border-t border-gray-100 flex justify-between items-center">
                                                                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Transaction</span>
                                                                                <span className="text-lg font-serif font-bold text-gray-900">
                                                                                    Rs {(order.totalPrice || 0).toLocaleString()}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex justify-center mt-6">
                                                                    <button 
                                                                        onClick={() => setOpen(null)}
                                                                        className="text-[10px] font-bold text-yellow-600 uppercase tracking-[0.3em] hover:text-gray-900 transition-colors"
                                                                    >
                                                                        Close Details
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="px-8 py-20 text-center">
                                                    <div className="flex flex-col items-center">
                                                        <MdReceipt className="text-5xl text-gray-100 mb-4" />
                                                        <p className="text-gray-400 font-light italic">No matching transaction records found.</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Order;
